import React, { useMemo } from 'react'
import moment from 'moment'
import { rem } from 'polished'
import styled from 'styled-components'

import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import Caption from 'components/Luxkit/Typography/Caption'
import FormatCurrency from 'components/Common/FormatCurrency'
import { mediaQueryUp } from 'components/utils/breakpoint'
import ResponsiveImage, { ImageParams } from 'components/Common/ResponsiveImage'
import { pluralizeToString } from 'lib/pluralize'
import ImageCarousel from 'components/Luxkit/Carousel/ImageCarousel'
import TextButton from 'components/Luxkit/Button/TextButton'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LineMapMarkerIcon from 'components/Luxkit/Icons/line/LineMapMarkerIcon'
import AutoTooltip, { AutoTooltipContent } from 'components/Common/AutoTooltip'
import MarkdownRender from 'components/Luxkit/MarkdownRender'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import FloatingBookmarkContainer from 'tripPlanner/components/Bookmark/Common/FloatingBookmarkContainer'
import OffsetBookmarkContainer from 'tripPlanner/components/Bookmark/Common/OffsetBookmarkContainer'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import Pill from 'components/Luxkit/Pill'
import { ValueWrapper } from 'components/Common/OfferPricing/styles'
import LinePricetagAltIcon from 'components/Luxkit/Icons/line/LinePricetagAltIcon'
import LineClockDollarIcon from 'components/Luxkit/Icons/line/LineClockDollarIcon'
import CruiseDiscountTermsAndConditions from 'components/Cruises/CruiseDiscountTermsAndConditions'
import { getCruiseOfferPriceVariation } from 'lib/Cruise/calculatingPrices'
import SolidCheckIcon from 'components/Luxkit/Icons/solid/SolidCheckIcon'
import ProductTypeLabel from 'components/Luxkit/Label/ProductTypeLabel'
import InlineGroup from 'components/Common/InlineGroup'
import PartnershipLabel from 'components/Luxkit/Label/PartnershipLabel'
import { VIRGIN_VELOCITY_PARTNERSHIP_CODE } from 'constants/partnerships'
import { connect } from 'react-redux'
import Label from 'components/Luxkit/Label/Label'
import LabelGroup from 'components/Luxkit/Label/LabelGroup'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'

const BadgeContainer = styled(LabelGroup)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`

const Cruise = styled.div`
  grid-template-columns: 1fr;
  grid-template-rows: ${rem(220)} 1fr;
  border-radius: ${props => props.theme.borderRadius.medium};
  border: 1px solid ${({ theme }) => theme.palette.neutral.five};
  background-color: ${(props) => props.theme.palette.neutral.eight};
  overflow: hidden;
  position: relative;

  ${mediaQueryUp.md} {
    display: grid;
    grid-template-columns: ${rem(500)} 1fr;
    grid-template-rows: 1fr;
  }
`

const TileMediaContainer = styled.div`
  display: flex;
  position: relative;
  height: ${rem(230)};

  ${mediaQueryUp.md} {
    height: 100%;
  }
`

const DetailsCaption = styled(BodyText)`
  align-items: center;
  display: flex;

  > * + *::before {
    color: ${({ theme }) => theme.palette.neutral.one};
    content: "·";
    margin: 0 ${rem(4)};
    speak-as: bullets;
    speak: none;
  }
`

const OperatorLogoImage = styled(ResponsiveImage)`
  width: 100%;
  height: 100%;

  ${mediaQueryUp.xs} {
    width: 80%;
  }
`

const LogoContainer = styled.div`
  display: flex;
  gap: ${rem(4)};
  flex-direction: row;
  justify-content: space-between;
  ${mediaQueryUp.md} {
    flex-direction: row;
    gap: ${rem(16)};
  }

  &:empty {
    display: none;
  }
`

const PriceSection = styled.div`
  display: flex;
  justify-content: end;
  flex: 1;
  align-items: flex-end;

  ${mediaQueryUp.xs} {
    flex-direction: column;
    gap: ${rem(16)};
    align-items: inherit;
  }

  ${mediaQueryUp.sm} {
    gap: ${rem(36)};
  }
`

const OperatorLogoArea = styled.div`
  display: none;

  ${mediaQueryUp.xs} {
    display: flex;
    justify-content: center;
  }
`

const PriceSummary = styled.div`
  position: relative;
  width: 100%;
`

const PriceSectionButton = styled(TextButton)`
  position: absolute;
  bottom: 0;
  right: 0;
  width: auto;

  ${mediaQueryUp.xs} {
    margin-top: ${rem(12)};
    position: static;
    width: 100%;
  }
`

const Description = styled.div`
  position: relative;
  padding: ${rem(20)};
  gap: ${rem(12)};
  display: flex;
  flex-direction: column;

  ${mediaQueryUp.sm} {
    padding: ${rem(20)} ${rem(24)} ${rem(20)} ${rem(28)};
    gap: ${rem(24)};
    grid-template-columns: 1fr ${rem(172)};
  }
`

const DescriptionBody = styled.div`
  display: flex;
  flex-grow: 1;
  justify-content: space-between;
  flex-direction: column;
  gap: ${rem(16)};

  ${mediaQueryUp.xs} {
    flex-direction: row;
    gap: ${rem(24)};
  }
`

const OperatorLogoMobile = styled.div`
  display: flex;
  max-width: ${rem(95)};
  max-height: ${rem(60)};

  ${mediaQueryUp.xs} {
    display: none;
  }
`

const DescriptionSection = styled(VerticalSpacer)`
  height: 100%;
  overflow: hidden;
  display: block;

  ${mediaQueryUp.xs} {
    display: flex;
    flex: 3;
  }

  ${mediaQueryUp.sm} {
    flex: 1.7;
  }
`

const DestinationDescriptionText = styled(MarkdownRender)`
  display: none;

  ${mediaQueryUp.sm} {
    display: block;
    margin-top: ${rem(12)};
  }
`

const ItineraryTooltip = styled(AutoTooltip)`
  display: none;

  > span {
    text-decoration: underline;

    &:hover + div {
      transition: opacity 0.3s ease !important;
    }
  }

  ${mediaQueryUp.sm} {
    display: inline-block;
  }
`
const DepartureDateTooltip = styled(AutoTooltip)`
  display: none;

  > span {
    text-decoration: underline;
    margin-left: ${rem(4)};

    &:hover + div {
      transition: opacity 0.3s ease !important;
    }
  }

  ${mediaQueryUp.sm} {
    display: inline-block;
  }
`

const DepartureDateWrapper = styled.div`
  display: flex;
  align-items: center;
`

const PromotionDescription = styled.div`
  margin-top: ${rem(12)};

  > * + * {
    margin-top: ${rem(4)};
  }
`

const MEDIA_IMAGE_PARAMS: ImageParams = {
  quality: 'eco',
  aspectRatio: '16:9',
  xsWidth: '100vw',
  smWidth: '100vw',
  mdWidth: '750px',
  lgWidth: '750px',
  xlWidth: '750px',
}

interface MappedProps {
  isVelocityEnabled: boolean;
}

interface Props {
  cruise: App.CruiseOffer;
}

function CruisesOfferTile(props: Props & MappedProps) {
  const { cruise, isVelocityEnabled } = props
  const { ship, departurePort, returnPort, itinerary, mainDepartureDetails } = cruise
  const { lowestOverallPriceDetails } = mainDepartureDetails
  const { promotion, discountPills } = lowestOverallPriceDetails
  const promotionDetails = promotion ?? cruise.fallbackPromotion

  const cities = itinerary?.map((event) => {
    const { port } = event
    if (!port) return
    return port.city || port.name.split(',')[0]
  }).filter((city) => city && city.length > 1)

  const countries = itinerary?.map((itineraryItem) => {
    const { port } = itineraryItem
    if (!port) return
    return port.countryCode
  }).filter(country => country && country.length)

  const placesCount = [...new Set(cities)].length
  const countriesCount = [...new Set(countries)].length

  const {
    primaryPrice,
    secondaryPrice,
  } = getCruiseOfferPriceVariation(cruise, lowestOverallPriceDetails)

  const hasTheSameStartAndEndPort = departurePort === returnPort
  const shipInfoDescription = ship.shipInfo?.find(info => info.dataTypeName === 'Summary')?.description
  const cruiseDescription = cruise?.description || shipInfoDescription || cruise.destinationDescription

  const parsedDescription = cruiseDescription
    ?.replace(/(<([^>]+)>)/gi, '')
    ?.split(' ')
    ?.slice(0, 35)
    ?.join(' ') + '...'

  const [earliestDepartureDate, ...laterDates] = useMemo(() => {
    const departureDates = (cruise.departureDates || [])
      .map((date) => moment(date))
      .sort((a, b) => a.unix() - b.unix())
      .map((date) => date.format('DD MMM YYYY'))

    if (mainDepartureDetails.departureDate) {
      const mainDepartureDate = moment(mainDepartureDetails.departureDate).format('DD MMM YYYY')
      return [
        ...new Set([mainDepartureDate, ...departureDates]),
      ]
    }

    return departureDates
  }, [cruise.departureDates, mainDepartureDetails.departureDate])

  const returnPortCity = useMemo(() => returnPort?.split(',')?.[0], [returnPort])
  const departurePortCity = useMemo(() => departurePort?.split(',')?.[0], [departurePort])

  return (
    <Cruise>
      <TileMediaContainer>
        <ImageCarousel
        topLeft={
          <BadgeContainer>
            <ProductTypeLabel productType={cruise.productType} />
            {isVelocityEnabled && <PartnershipLabel canEarn canUse variant="icon" />}
          </BadgeContainer>}
          images={cruise.images}
          imageParams={MEDIA_IMAGE_PARAMS}
          eagerLoadFirstImage
          />
      </TileMediaContainer>
      <Description>
        <DescriptionBody>
          <DescriptionSection gap={8}>
            <VerticalSpacer gap={4}>
              <LabelGroup>
                {promotionDetails?.isNew && <Label kind="tertiary" variant="ghost">NEW</Label>}
                {!!promotionDetails?.isEndingIn && promotionDetails.isEndingIn <= 7 && (
                  <Label kind="primary" variant="urgency">
                    OFFER ENDS IN {pluralizeToString('day', promotionDetails.isEndingIn).toLocaleUpperCase()}
                  </Label>
                )}
              </LabelGroup>
              <BodyTextBlock variant="medium" startIcon={<CSSBreakpoint min="sm" as={ LineMapMarkerIcon} />}>
                {hasTheSameStartAndEndPort && (
                  <span>
                    Starts and ends in <b>{returnPortCity}</b>
                  </span>
                )}
                {!hasTheSameStartAndEndPort && (
                  <span>
                    Starts in <b>{departurePortCity}</b>. Ends in <b>{returnPortCity}</b>
                  </span>
                )}
              </BodyTextBlock>

              <Heading variant="heading4" lineClamp={2}>
                {cruise.name}
              </Heading>
              <BodyText variant="small">
                Operated by: <b>{cruise.cruiseLine.name}</b>
              </BodyText>
              <LogoContainer>
                <BodyText variant="small">
                  Cruise ship: <b>{cruise.ship.name}</b>
                </BodyText>
                {cruise.cruiseLine.imageId && (
                  <OperatorLogoMobile>
                    <OperatorLogoImage
                      id={cruise.cruiseLine.imageId}
                      xsWidth="75px"
                      smWidth="150px"
                      quality="eco"
                      alt={cruise.cruiseLine.name}
                      role="presentation"
                    />
                  </OperatorLogoMobile>
                )}
              </LogoContainer>

              <DetailsCaption variant="small" weight="bold">
                <div>{pluralizeToString('night', cruise.duration)}</div>
                {!!placesCount && <div>{pluralizeToString('port', placesCount)}</div>}
                {!!countriesCount && <div>{pluralizeToString('country', countriesCount)}</div>}
                <ItineraryTooltip>
                  View itinerary
                  <AutoTooltipContent width={250} type="dark" position="below">
                    <BodyText variant="small">
                      {[...new Set(cities)].join(' - ')}
                    </BodyText>
                  </AutoTooltipContent>
                </ItineraryTooltip>
              </DetailsCaption>

              <VerticalSpacer gap={12}>
                {promotionDetails?.sellingPoints && <PromotionDescription>
                  <BodyText weight="semi-bold" variant="medium">
                    Inclusions (select packages only)
                  </BodyText>
                  {promotionDetails?.sellingPoints.map(
                    (point, index) => {
                      const isHighlighted = index === 0 && !promotionDetails.onBoardCredit
                      if (!isHighlighted) {
                        return (
                          <BodyTextBlock key={point} weight="normal" variant="small" startIcon={<SolidCheckIcon/>}>
                            {point}
                          </BodyTextBlock>
                        )
                      }
                    },
                  )}
                </PromotionDescription>
                }
                {promotionDetails?.sellingPoints && !promotionDetails?.onBoardCredit && (
                  <PromotionDescription>
                    <BodyTextBlock colour="highlight" weight="bold" variant="medium" startIcon={<LinePricetagAltIcon size="S"/>}>
                      {promotionDetails?.sellingPoints[0]}
                    </BodyTextBlock>
                  </PromotionDescription>
                )}
                {promotionDetails?.onBoardCredit && (
                  <PromotionDescription>
                    <BodyTextBlock colour="highlight" weight="bold" variant="medium" startIcon={<LinePricetagAltIcon size="S"/>}>
                      Up to <FormatCurrency currency={promotionDetails.onBoardCredit.currencyCode} value={promotionDetails.onBoardCredit.amount ?? 0} /> onboard credit available
                    </BodyTextBlock>
                  </PromotionDescription>
                )}

                {parsedDescription && !promotionDetails && (
                  <DestinationDescriptionText
                    type="compact"
                    fontSize="medium"
                    fontWeight="normal"
                    content={parsedDescription}
                  />
                )}

                <DepartureDateWrapper>
                  <BodyText variant="medium" weight="bold">
                    Departing on {earliestDepartureDate}

                    {laterDates?.length > 0 &&
                      <DepartureDateTooltip>
                        and more
                        <AutoTooltipContent width={250} type="dark" position="above">
                          <BodyText variant="small">
                            {laterDates.join(', ')}
                          </BodyText>
                        </AutoTooltipContent>
                      </DepartureDateTooltip>
                    }
                  </BodyText>
                </DepartureDateWrapper>
              </VerticalSpacer>

            </VerticalSpacer>
          </DescriptionSection>

          <PriceSection>
            <TripGuard>
              <CSSBreakpoint max="sm">
                <OffsetBookmarkContainer>
                  <BookmarkButton offer={cruise} />
                </OffsetBookmarkContainer>
              </CSSBreakpoint>
              <CSSBreakpoint min="md">
                <FloatingBookmarkContainer>
                  <BookmarkButton offer={cruise} />
                </FloatingBookmarkContainer>
              </CSSBreakpoint>
            </TripGuard>
            {cruise.cruiseLine.imageId && (
              <OperatorLogoArea>
                <OperatorLogoImage
                  id={cruise.cruiseLine.imageId}
                  xsWidth="75px"
                  smWidth="150px"
                  quality="eco"
                  alt={cruise.cruiseLine.name}
                  role="presentation"
                />
              </OperatorLogoArea>
            )}
            <PriceSummary>
              <VerticalSpacer gap={8}>
                {!cruise.disableDeposits && (
                  <div>
                    <BodyText variant="small">
                      {promotionDetails?.deposit && <Pill kind="tertiary" variant="primary" startIcon={<LineClockDollarIcon />}>
                        Secure with a {promotionDetails.deposit.formattedValue} deposit
                      </Pill>}
                      {!promotionDetails?.deposit && <Pill kind="tertiary" variant="primary" startIcon={<LineClockDollarIcon />}>
                        Deposits available
                      </Pill>}
                    </BodyText>
                  </div>
                )}

                <div>
                  {lowestOverallPriceDetails.cabinCategory && <Caption variant="medium" colour="neutral-three">
                    {lowestOverallPriceDetails.cabinCategory} from
                  </Caption>}
                  <InlineGroup vertical="baseline">
                    <Heading variant="heading3" as="span">
                      <FormatCurrency currency={primaryPrice.currencyCode} value={primaryPrice.total ?? 0} />
                    </Heading>
                    <BodyText variant="medium" weight="normal" as="span">
                      /{primaryPrice.type}
                    </BodyText>
                  </InlineGroup>

                  {!secondaryPrice && <Caption variant="medium" weight="normal"> Twin Share </Caption>}

                  {secondaryPrice && <>
                    <Caption variant="large" colour="neutral-three" as="spam">
                      <FormatCurrency currency={secondaryPrice.currencyCode} value={secondaryPrice.total ?? 0} />
                    </Caption>
                    <Caption variant="large" colour="neutral-three" as="spam">
                      /{secondaryPrice.type} · Twin Share
                    </Caption>
                  </>}

                  <Caption variant="medium" colour="neutral-three">
                    Includes taxes & fees
                  </Caption>

                  {discountPills?.price && discountPills?.discountPercentage && (
                    <ValueWrapper variant="large" weight="bold">
                      <Caption variant="medium" weight="bold" colour="neutral-one">
                        Valued up to <FormatCurrency value={discountPills.price}/>
                      </Caption>
                      <Pill
                        kind="primary"
                        variant="success">
                        -{discountPills.discountPercentage}%</Pill>
                      <CruiseDiscountTermsAndConditions />
                    </ValueWrapper>
                  )}
                </div>

                <PriceSectionButton
                  kind="primary"
                  nonInteractable
                >
                  View offer
                </PriceSectionButton>
              </VerticalSpacer>
            </PriceSummary>
          </PriceSection>
        </DescriptionBody>
      </Description>
    </Cruise>
  )
}

const mapStateToProps = (state: App.State): MappedProps => ({
  isVelocityEnabled: state.geo.partnership?.code === VIRGIN_VELOCITY_PARTNERSHIP_CODE,
})

export default connect(mapStateToProps)(CruisesOfferTile)
